import React, { useState, useEffect, use, useMemo } from "react";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import editIcon from "../assets/Images/Edit.png";
import viewIcon from "../assets/Images/View.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import crossIcon from "../assets/icons/crossIcon.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import productIcon from "../assets/icons/ProductIcon.png"; // Assuming you have a product icon
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

const BASE_URL = `${process.env.REACT_APP_API_URL}/products`;

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    product_name: "",
    product_description: "",
  });
  const [productsStats, setProductsStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [editingProductId, setEditingProductId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Product stats list for dashboard
  const productStatsList = [
    { label: "Total Products", key: "total" },
    { label: "Active Products", key: "active" },
    { label: "Inactive Products", key: "inactive" },
  ];

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    // console.log("Sorting by:", sortOrder);
    // console.log("Filtering by day:", dayFilter);
  };

  const fetchProducts = async () => {
    if (!token) {
      showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);
      return;
    }
    try {
      const response = await api.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products", error);
      showErrorToast(TOAST_MESSAGES.PRODUCT.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products
      .filter((product) => {
        const lower = searchTerm.toLowerCase();
        const productName = product.product_name
          ? product.product_name.toLowerCase()
          : "";
        const productDesc = product.product_description
          ? product.product_description.toLowerCase()
          : "";
        return productName.includes(lower) || productDesc.includes(lower);
      })
      .filter((product) => {
        if (!dayFilter) return true;
        const productDate = new Date(product.created_timestamp);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dayFilter === "Today") return productDate >= today;
        if (dayFilter === "Yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          return productDate >= yesterday && productDate < today;
        }
        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.updated_timestamp);
        const dateB = new Date(b.updated_timestamp);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
  }, [products, searchTerm, dayFilter, sortOrder]);

  const totalEntries = filteredProducts.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredProducts.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;
    try {
      await api.delete(`${BASE_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchProducts();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      product_name: product.product_name,
      product_description: product.product_description || "",
    });
    setEditingProductId(product.product_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (product) => {
    setFormData({
      product_name: product.product_name,
      product_description: product.product_description || "",
    });
    setEditingProductId(product.product_id);
    setViewMode(true);
    setShowForm(true);
  };

  const handleChangeStatus = async (product) => {
    const currentStatus = product.product_status; // Assuming "1" or "0"
    const newStatus = currentStatus === "1" ? "Inactive" : "Active";

    try {
      await api.put(
        `${BASE_URL}/change-status/${product.product_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchProducts();
      showSuccessToast(
        `${TOAST_MESSAGES.PRODUCT.STATUS_CHANGE_SUCCESS} to ${newStatus}`,
      );
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.PRODUCT.STATUS_CHANGE_ERROR);
    }
  };

  useEffect(() => {
    const calculateStats = () => {
      const total = filteredProducts.length;
      const active = filteredProducts.filter(
        (p) => p.product_status === "1",
      ).length;
      const inactive = filteredProducts.filter(
        (p) => p.product_status === "0",
      ).length;
      setProductsStats({ total, active, inactive });
    };
    calculateStats();
  }, [filteredProducts]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // start loading

    try {
      const url = editingProductId
        ? `${BASE_URL}/${editingProductId}`
        : BASE_URL;
      const method = editingProductId ? "PUT" : "POST";
      await api({
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      });
      setFormData({ product_name: "", product_description: "" });
      setShowForm(false);
      setEditingProductId(null);
      fetchProducts();
      showSuccessToast(
        editingProductId
          ? TOAST_MESSAGES.PRODUCT.UPDATE_SUCCESS
          : TOAST_MESSAGES.PRODUCT.CREATE_SUCCESS,
      );
    } catch (err) {
      console.error(err);
      showErrorToast(
        editingProductId
          ? TOAST_MESSAGES.PRODUCT.UPDATE_ERROR
          : TOAST_MESSAGES.PRODUCT.CREATE_ERROR,
      );
    } finally {
      setIsSubmitting(false); // stop loading
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({ product_name: "", product_description: "" });
    setEditingProductId(null);
    setViewMode(false);
  };

  const exportExcel = () => {
    const wsData = [
      ["Product", "Description", "Status"],
      ...filteredProducts.map((p) => [
        p.product_name,
        p.product_description || "",
        p.product_status === "1" ? "Active" : "Inactive",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "product_management.xlsx");
  };

  return (
    <div className="mx-2 pt-1  h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {productStatsList.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-2">
              <img
                src={productIcon}
                alt={item.label}
                style={{
                  width: "22px",
                  height: "22px",
                }}
                className="icon-image1"
              />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{productsStats[item.key]}</h2>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold refresh-prompts" style={{ fontSize: "20px" }}>
          Product Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Products"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts px-0">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bolder refresh-prompts px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Product"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table role-table table-sm w-100">
              <thead>
                <tr>
                  <SortableHeader
                    column="product_name"
                    label="Product"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th>Description</th>
                  <SortableHeader
                    column="product_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((p) => (
                  <tr key={p.product_id}>
                    <td
                      title={p.product_name}
                      style={{
                        maxWidth: "190px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {p.product_name
                        ? p.product_name.charAt(0).toUpperCase() +
                          p.product_name.slice(1)
                        : "—"}
                    </td>
                    <td title={p.product_description}>
                      {p.product_description || "—"}
                    </td>
                    <td>{p.product_status === "1" ? "Active" : "Inactive"}</td>
                    <td>{formatDateToUserTimeZone(p.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(p)}
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(p)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(p)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {/* <div className="d-flex justify-content-between align-items-center mt-2 table-footer"> */}
          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <form onSubmit={handleFormSubmit}>
                <div className="modal-header  justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewMode
                      ? "View Product"
                      : editingProductId
                        ? "Update Product"
                        : "Add Product"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn "
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label required-label">Product</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.product_name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          product_name: e.target.value,
                        })
                      }
                      required
                      disabled={viewMode}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label required-label">
                      Description
                    </label>
                    <textarea
                      className="input-textarea1  p-1"
                      required
                      value={formData.product_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          product_description: e.target.value,
                        })
                      }
                      disabled={viewMode}
                    />
                  </div>
                </div>
                <div className="modal-footer ">
                  {!viewMode && (
                    <button
                      type="submit"
                      className=" rounded custom-btn   border-none py-2 fw-bold"
                      disabled={isSubmitting} // prevent double submits
                    >
                      {isSubmitting
                        ? editingProductId
                          ? "Updating..."
                          : "Adding..."
                        : editingProductId
                          ? "Update Product"
                          : "Add Product"}
                    </button>
                  )}
                  {viewMode && (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
